import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import { Separator } from "../components/ui/separator";
import API from "../lib/api";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect") || "/";

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await API.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setForgotSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isLogin) {
        const data = await login(email, password);
        if (data.role === "admin") navigate("/admin");
        else navigate(redirect === "checkout" ? "/checkout" : "/");
      } else {
        await register(name, email, password);
        navigate(redirect === "checkout" ? "/checkout" : "/");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  if (forgotMode) {
    return (
      <div className="min-h-screen pt-28 pb-24 px-6 flex items-center justify-center" data-testid="forgot-password-page">
        <div className="w-full max-w-md">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3 font-medium">Account Recovery</p>
            <h1 className="font-['Cormorant_Garamond'] text-4xl font-light text-[#FDFBF7] tracking-tighter">Forgot Password</h1>
          </div>
          {forgotSent ? (
            <div className="text-center space-y-6" data-testid="forgot-success-message">
              <p className="text-[#FDFBF7] text-sm font-light leading-relaxed">
                If an account exists with <span className="text-[#D4AF37]">{email}</span>, a password reset link has been sent. Please check your inbox.
              </p>
              <button
                onClick={() => { setForgotMode(false); setForgotSent(false); setError(""); }}
                className="text-[#D4AF37] hover:text-[#E5C158] text-sm transition-colors duration-300"
                data-testid="back-to-login-btn"
              >
                Back to Sign In
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgotSubmit} className="space-y-6" data-testid="forgot-password-form">
              <p className="text-[#A1A1AA] text-sm font-light leading-relaxed">
                Enter your email address and we'll send you a link to reset your password.
              </p>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-2 block font-medium">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-sm text-[#FDFBF7] placeholder-zinc-500 focus:outline-none focus:border-[#D4AF37] transition-colors duration-300 font-light"
                  placeholder="you@example.com"
                  data-testid="forgot-email-input"
                />
              </div>
              {error && <p className="text-red-400 text-sm font-light" data-testid="forgot-error">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="btn-luxury w-full bg-[#D4AF37] text-black py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-[#E5C158] transition-colors duration-500 disabled:opacity-50"
                data-testid="forgot-submit-btn"
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </button>
              <p className="text-center">
                <button
                  type="button"
                  onClick={() => { setForgotMode(false); setError(""); }}
                  className="text-[#A1A1AA] hover:text-[#FDFBF7] text-sm transition-colors duration-300"
                  data-testid="forgot-back-btn"
                >
                  Back to Sign In
                </button>
              </p>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-24 px-6 flex items-center justify-center" data-testid="auth-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3 font-medium">
            {isLogin ? "Welcome Back" : "Join Us"}
          </p>
          <h1 className="font-['Cormorant_Garamond'] text-4xl font-light text-[#FDFBF7] tracking-tighter">
            {isLogin ? "Sign In" : "Create Account"}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="auth-form">
          {!isLogin && (
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-2 block font-medium">Full Name</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                required={!isLogin}
                className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-sm text-[#FDFBF7] placeholder-zinc-500 focus:outline-none focus:border-[#D4AF37] transition-colors duration-300 font-light"
                placeholder="La'Daena Londo"
                data-testid="register-name-input"
              />
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-2 block font-medium">Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-sm text-[#FDFBF7] placeholder-zinc-500 focus:outline-none focus:border-[#D4AF37] transition-colors duration-300 font-light"
              placeholder="you@example.com"
              data-testid="auth-email-input"
            />
          </div>
          <div className="relative">
            <label className="text-xs uppercase tracking-[0.15em] text-[#A1A1AA] mb-2 block font-medium">Password</label>
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-sm text-[#FDFBF7] placeholder-zinc-500 focus:outline-none focus:border-[#D4AF37] transition-colors duration-300 font-light pr-10"
              placeholder="Enter password"
              data-testid="auth-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-0 top-8 text-[#A1A1AA] hover:text-[#FDFBF7] transition-colors"
              data-testid="toggle-password-visibility"
            >
              {showPassword ? <EyeSlash size={18} weight="thin" /> : <Eye size={18} weight="thin" />}
            </button>
          </div>

          {error && (
            <p className="text-red-400 text-sm font-light" data-testid="auth-error">{error}</p>
          )}

          {isLogin && (
            <p className="text-right -mt-2">
              <button
                type="button"
                onClick={() => { setForgotMode(true); setError(""); }}
                className="text-xs text-[#A1A1AA] hover:text-[#D4AF37] transition-colors duration-300 tracking-wide"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </button>
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn-luxury w-full bg-[#D4AF37] text-black py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-[#E5C158] transition-colors duration-500 disabled:opacity-50"
            data-testid="auth-submit-btn"
          >
            {loading ? "Please wait..." : isLogin ? "Sign In" : "Create Account"}
          </button>
        </form>

        <Separator className="bg-white/5 my-8" />

        <p className="text-center text-sm text-[#A1A1AA] font-light">
          {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
          <button
            onClick={() => { setIsLogin(!isLogin); setError(""); }}
            className="text-[#D4AF37] hover:text-[#E5C158] transition-colors duration-300"
            data-testid="auth-toggle-btn"
          >
            {isLogin ? "Create one" : "Sign in"}
          </button>
        </p>
      </div>
    </div>
  );
}
